import type {
  ColumnMeta,
  RoutineMeta,
  SchemaSnapshot,
  TableMeta,
  UserTypeMeta,
} from '@/core/engines/intellisenseTypes';
import type {
  PgSchemaSnapshot,
  PgSchemaTableEntry,
} from '@/features/connections/services/language-services/pgSchemaTypes';
import { getCachedPgSchema } from '@/features/connections/services/language-services/pgSchemaCache';
import { classifyJsonColumn } from '@/features/sql-resolver/json/classify/classifyJsonColumn';
import { projectRoutines } from './projectionHelpers';

/**
 * Pure projection: native PG snapshot → bus `SchemaSnapshot`.
 *
 * Uses the schema-qualified `tables` field (added in PRD: PostgreSQL
 * Resolver — Core slice). The legacy `columnsByTableLower` map is left
 * untouched for back-compat with `pgCompletion.ts`. `nullable` defaults to
 * `true` and `ordinal` is the array index — `PgSchemaColumn` doesn't carry
 * either field today.
 *
 * `searchPath` flows through verbatim so the resolver can prefer relations
 * from earlier-in-search_path schemas at unqualified positions.
 */
export function projectPgSnapshot(native: PgSchemaSnapshot): SchemaSnapshot {
  const tables: TableMeta[] = [];
  const schemaSet = new Set<string>();
  for (const entry of native.tables) {
    schemaSet.add(entry.schema);
    tables.push(projectPgTableEntry(entry, native.database));
  }

  const routines: RoutineMeta[] = projectRoutines(native.routinesByNameLower, native.database);

  // `pg_type` names travel with their schema and kind (enum / domain / range /
  // composite) — the schema-qualified type slot (`price public.▮`) has to emit
  // only the types that actually live in the named namespace.
  const userTypes: UserTypeMeta[] = (native.userTypes ?? []).map((t) => ({
    name: t.name,
    schema: t.schema,
    kind: t.kind,
  }));

  return {
    engineId: 'pgsql',
    connectionId: native.connectionId,
    database: native.database,
    schemas: Array.from(schemaSet).sort(),
    tables,
    routines,
    foreignKeys: native.foreignKeys ?? [],
    // An unreadable search_path is left out, so the resolver falls back to
    // PostgreSQL's default (like the VQE); an empty one that was read stays.
    ...(native.searchPathKnown === false ? {} : { searchPath: native.searchPath }),
    extensions: native.extensions ?? [],
    serverVersion: native.serverVersion || undefined,
    ...(userTypes.length > 0 ? { userTypes } : {}),
    // `relkind = 'S'` rows — the `nextval('▮')` / `currval('▮')` /
    // `setval('▮', …)` literal slot's population. Omitted when the database
    // declares none so the field stays undefined for sequence-less databases.
    ...(native.sequences && native.sequences.length > 0
      ? { sequences: native.sequences }
      : {}),
    // Property graphs (`pg_propgraph_*`, PG19+) forwarded verbatim — the native
    // shape is already `PropertyGraphMeta`. Omitted when the database declares
    // none so the field stays undefined for non-graph databases (and pre-19
    // servers, where the introspection version-guards to []).
    ...(native.propertyGraphs && native.propertyGraphs.length > 0
      ? { propertyGraphs: native.propertyGraphs }
      : {}),
    builtAt: native.builtAt,
  };
}

/**
 * Project ONE native relation entry into its bus-shaped `TableMeta`. Split out
 * of `projectPgSnapshot` for the tiered snapshot load (SLICES.md § S7 items 1
 * + 8): phase 2 merges columns page by page and re-projects only the relations
 * a page touched, instead of re-running the whole snapshot projection per page.
 */
export function projectPgTableEntry(
  entry: PgSchemaTableEntry,
  database: string,
): TableMeta {
  const columns: ColumnMeta[] = entry.columns.map((c, i) => {
    const classification = classifyJsonColumn(c.dataType, 'postgres');
    const base: ColumnMeta = {
      name: c.name,
      dataType: c.dataType,
      nullable: true,
      isPrimaryKey: c.isPrimaryKey,
      ordinal: i,
      ...(c.enumValues && c.enumValues.length > 0 ? { enumValues: c.enumValues } : {}),
      ...(c.isIdentity ? { isIdentity: true } : {}),
      ...(c.hasDefault ? { hasDefault: true } : {}),
    };
    return classification.kind === 'not-json'
      ? base
      : { ...base, jsonClassification: classification };
  });
  return {
    name: entry.name,
    schema: entry.schema,
    database,
    // `pg_class.relkind`, forwarded by the introspection. Absent (fixtures
    // that predate the catalog scan, or a failed probe) → 'table', the
    // unconditional value every entry used to carry.
    kind: entry.kind ?? 'table',
    columns,
  };
}

/**
 * Synchronous read of the bus-shaped snapshot for `(connectionId, database)`.
 * The PG cache only holds one snapshot per connection (latest selected DB);
 * if `database` doesn't match the cached snapshot's `database`, return null
 * to keep the orchestrator from binding the wrong-database snapshot.
 */
export function buildSnapshot(
  connectionId: string,
  database: string,
): SchemaSnapshot | null {
  const native = getCachedPgSchema(connectionId);
  if (!native || native.database !== database) return null;
  return projectPgSnapshot(native);
}
